// src/stores/tenantStore.ts
//
// Identidade do tenant atual (multi-tenant, specs/001-multi-tenant US3).
// Carregada ANTES do mount (plugins/branding.ts) com cache por hostname em
// sessionStorage — evita flash de tema errado em recargas. Em falha, aplica
// fallback NEUTRO (nunca a identidade de outro tenant).

import { fetchPublicBranding, type TenantPublicInfo } from '@/services/brandingApi';
import { defineStore } from 'pinia';

const CACHE_KEY = `hub-tenant-branding:${window.location.hostname}`;

/** Fallback neutro — usado apenas quando a API de branding está inacessível. */
const NEUTRAL: TenantPublicInfo = {
    slug: '',
    nome: 'Hub',
    branding: { titulo: 'Hub' },
    enabled_modules: [],
    // Em falha transitória não escondemos o login corporativo
    sso_enabled: true,
    // Fallback nunca é o master — administração de tenants some em caso de falha
    is_master: false,
};

function readCache(): TenantPublicInfo | null {
    try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        return raw ? (JSON.parse(raw) as TenantPublicInfo) : null;
    } catch {
        return null;
    }
}

export const useTenantStore = defineStore('tenant', {
    state: () => ({
        info: readCache(),
        loaded: false,
    }),

    getters: {
        slug: (s) => s.info?.slug ?? NEUTRAL.slug,
        nome: (s) => s.info?.nome ?? NEUTRAL.nome,
        branding: (s) => s.info?.branding ?? NEUTRAL.branding,
        enabledModules: (s) => s.info?.enabled_modules ?? [],
        ssoEnabled: (s) => s.info?.sso_enabled ?? NEUTRAL.sso_enabled,
        isMaster: (s) => s.info?.is_master === true,
        titulo(): string {
            return this.branding.titulo || this.nome || 'Hub';
        },
    },

    actions: {
        /**
         * Busca o branding do tenant do hostname. Chamado antes do mount.
         * Falha de rede → mantém cache (se houver) ou aplica fallback neutro.
         */
        async fetchBranding(): Promise<void> {
            try {
                const info = await fetchPublicBranding();
                this.info = info;
                try {
                    sessionStorage.setItem(CACHE_KEY, JSON.stringify(info));
                } catch {
                    /* sessionStorage indisponível — segue sem cache */
                }
            } catch (error) {
                console.warn('⚠️ Branding do tenant indisponível, usando fallback', error);
                if (!this.info) this.info = { ...NEUTRAL };
            } finally {
                this.loaded = true;
            }
        },
    },
});
